import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import {
  formatDate,
  isTruthy,
  openErrorNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import { store } from "../../utils/store";
import { getBooking } from "./CustomerService";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../Shared/Organization/OrganizationSelect";
import { selectActiveOrganizationId } from "../../redux/authSlice";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const CustomerList = () => {
  const history = useHistory();
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    handleTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, search, activeOrganizationId]);

  const handleTableData = async () => {
    try {
      setLoading(true);
      const orgId =
        activeOrganizationId &&
        activeOrganizationId !== "all" &&
        activeOrganizationId !== "*"
          ? String(activeOrganizationId)
          : undefined;
      const response = await getBooking({
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search,
        userId: store.getState().auth.userId,
        organizationId: orgId,
      });
      setTableData(response?.data || []);
      setTotalRows(response?.total || response?.data?.length || 0);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const getCustomerId = (row: any) => {
    const raw = row?._id ?? row?.id;
    if (raw == null) return "";
    if (typeof raw === "string" || typeof raw === "number") return String(raw);
    if (typeof raw === "object" && raw.$oid) return String(raw.$oid);
    if (typeof raw?.toString === "function") {
      const s = raw.toString();
      if (s && s !== "[object Object]") return s;
    }
    return "";
  };

  const openCustomerDetails = (row: any) => {
    const id = getCustomerId(row);
    if (!id) return;
    history.push(`/customer/${id}`, { customer: row });
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 240,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const name = params.row?.name || "?";
        const initial = String(name).trim().charAt(0).toUpperCase() || "?";
        const avatarPalette = [
          { bg: "#F9F0F4", color: "#6B1630" },
          { bg: "#DBEAFE", color: "#2563EB" },
          { bg: "#D1FAE5", color: "#059669" },
          { bg: "#FFEDD5", color: "#EA580C" },
          { bg: "#FEF3C7", color: "#D97706" },
          { bg: "#FCE7F3", color: "#DB2777" },
          { bg: "#CCFBF1", color: "#0D9488" },
        ];
        let hash = 0;
        for (let i = 0; i < String(name).length; i++) {
          hash = String(name).charCodeAt(i) + ((hash << 5) - hash);
        }
        const avatar = avatarPalette[Math.abs(hash) % avatarPalette.length];

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openCustomerDetails(params.row);
            }}
            className="flex w-full items-center gap-3 text-left transition hover:opacity-80"
          >
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold"
              style={{ backgroundColor: avatar.bg, color: avatar.color }}
            >
              {initial}
            </span>
            <div className="min-w-0 flex flex-col items-start gap-0.5">
              <span className="truncate text-sm font-semibold capitalize text-[#A02448] underline-offset-2 hover:underline">
                {params.row?.name || "—"}
              </span>
              <span className="truncate text-xs text-[#9CA3AF]">
                {params.row?.email || params.row?.customer_email || ""}
              </span>
            </div>
          </button>
        );
      },
    },
    {
      headerName: "Mobile Number",
      field: "phoneNumber",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.phoneNumber || params.row?.phone || "—"}
        </span>
      ),
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.source || "—"}
        </span>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
          fallbackLabel={
            params.row?.organizationName || params.row?.organization?.name
          }
        />
      ),
    },
    {
      headerName: "Project Name",
      field: "projectName",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-medium text-[#111827]">
          {params.row?.projectName ||
            params.row?.project_name ||
            "—"}
        </span>
      ),
    },
    {
      headerName: "Created At",
      field: "createdAt",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#6B7280]">
          {params.row?.createdAt ? formatDate(params.row.createdAt) : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xl font-bold tracking-tight text-[#111827]">
            Customer List
          </h3>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage your customers here.
          </p>
        </div>
        <div className="w-full max-w-md sm:w-[510px]">
          <YhInput
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            placeholder="Search by name"
            type="search"
            autoFocus={isSearchFocused}
            startAdornment={<SearchSvg />}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={tableData}
          getRowId={(row) => getCustomerId(row) || String(Math.random())}
          rowHeight={76}
          loading={loading}
          emptyMessage="No customers yet"
          className="h-full min-h-[280px]"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={totalRows}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
          paginationLabel="customers"
          onRowClick={(row) => openCustomerDetails(row)}
        />
      </div>
    </div>
  );
};

export default CustomerList;
